import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext';
import { ChevronRightIcon, PhoneIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import versionData from '../../version.json';

export const AdminSettings: React.FC = () => {
    const { actualTheme } = useTheme();
    const { showSuccess, showError } = useToast();
    const [phones, setPhones] = useState<string[]>([]);
    const [newPhone, setNewPhone] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const data = await apiService.getSettings();
            setPhones(data.admin_whatsapp_phones || []);
        } catch (error) {
            showError('Failed to load system settings', 'Error');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiService.updateSettings({ admin_whatsapp_phones: phones });
            showSuccess('Settings synced successfully', 'Saved');
        } catch (error) {
            showError('Failed to save settings', 'Error');
        } finally {
            setSaving(false);
        }
    };

    const addPhone = () => {
        if (!newPhone) return;
        if (!phones.includes(newPhone)) {
            setPhones([...phones, newPhone]);
        }
        setNewPhone('');
    };

    const removePhone = (phone: string) => {
        setPhones(phones.filter(p => p !== phone));
    };

    const isDark = actualTheme === 'dark';

    if (loading) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-100'} p-4 flex justify-center items-center`}>
                <div className="w-8 h-8 rounded-full border-4 border-gray-300 border-t-blue-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-[#000000] text-white' : 'bg-[#F2F2F7] text-black'} pb-24`}>
            {/* Native Mobile Header */}
            <div className={`sticky top-0 z-10 px-4 py-3 flex items-center justify-between border-b ${isDark ? 'bg-black/80 border-gray-800' : 'bg-[#F2F2F7]/80 border-gray-300'} backdrop-blur-xl`}>
                <h1 className="text-[34px] font-bold tracking-tight">Settings</h1>
            </div>

            <div className="px-4 py-6 space-y-8 max-w-2xl mx-auto">
                
                {/* Section: Notifications */}
                <div>
                    <h2 className="text-[13px] uppercase tracking-wide text-gray-500 font-medium ml-4 mb-2">Notifications</h2>
                    <div className={`rounded-[10px] overflow-hidden ${isDark ? 'bg-[#1C1C1E]' : 'bg-white'}`}>
                        <div className="p-4">
                            <p className={`text-[15px] mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                WhatsApp numbers that will receive instant alerts for confirmed bookings.
                            </p>
                            
                            <div className="space-y-3">
                                {phones.map((phone, i) => (
                                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${isDark ? 'bg-black/50' : 'bg-gray-50'}`}>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                                                <PhoneIcon className="w-4 h-4 text-green-500" />
                                            </div>
                                            <span className="text-[17px] font-medium font-mono">{phone}</span>
                                        </div>
                                        <button onClick={() => removePhone(phone)} className="text-[15px] text-red-500 font-medium active:opacity-50">
                                            Remove
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <div className="mt-4 flex gap-2">
                                <input
                                    type="text"
                                    placeholder="+2547XXXXXXXX"
                                    value={newPhone}
                                    onChange={(e) => setNewPhone(e.target.value)}
                                    className={`flex-1 text-[17px] px-4 py-3 rounded-lg border-none focus:ring-0 ${isDark ? 'bg-black/50 text-white placeholder-gray-500' : 'bg-gray-50 text-black placeholder-gray-400'}`}
                                />
                                <button
                                    onClick={addPhone}
                                    disabled={!newPhone}
                                    className="px-6 py-3 bg-blue-500 text-white text-[17px] font-semibold rounded-lg active:opacity-80 disabled:opacity-50"
                                >
                                    Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section: App Info */}
                <div>
                    <h2 className="text-[13px] uppercase tracking-wide text-gray-500 font-medium ml-4 mb-2">About App</h2>
                    <div className={`rounded-[10px] overflow-hidden ${isDark ? 'bg-[#1C1C1E]' : 'bg-white'}`}>
                        <div className={`flex items-center justify-between p-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
                            <div className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded bg-blue-500 flex items-center justify-center">
                                    <InformationCircleIcon className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-[17px]">SightTrails Version</span>
                            </div>
                            <span className="text-[17px] text-gray-400 font-mono">v{versionData.version}</span>
                        </div>
                        <div className="flex items-center justify-between p-4">
                            <div className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded bg-green-500 flex items-center justify-center">
                                    <CheckCircleIcon className="w-5 h-5 text-white" />
                                </div>
                                <span className="text-[17px]">Service Worker</span>
                            </div>
                            <span className="text-[17px] text-gray-400">Active</span>
                        </div>
                    </div>
                    <p className="px-4 py-2 text-[13px] text-gray-500 text-center mt-2">
                        SightTrails Safaris PWA • Native Experience
                    </p>
                </div>

                {/* Save Button */}
                <div className="pt-6">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="w-full py-4 rounded-[14px] bg-blue-500 text-white text-[17px] font-bold active:bg-blue-600 transition-colors shadow-sm disabled:opacity-50"
                    >
                        {saving ? 'Syncing...' : 'Save Settings'}
                    </button>
                </div>

            </div>
        </div>
    );
};
